import type { EngineEvent } from '../../interfaces/websocket-events.interface'
import { microsToDollars } from '../../utils/billing.util'
import { logger } from '../../utils/logger.util'
import { agentsApiSlice } from '../agents-api.store'
import { updateTenantBalance } from '../slices/billing.slice'
import {
  addAgentReportEntity,
  appendToItemStream,
  clearConversationStopping,
  clearLocallyCreatedFlag,
  clearOrchestrationActive,
  completeAllConversationEntityStreaming,
  confirmAllPendingInConversation,
  createContentStreamEntity,
  failItemStreaming,
  finalizeEntityStreaming,
  markConversationAsLocallyCreated,
  movePendingToConversation,
  setActiveConversation,
  setOrchestrationActive,
} from '../slices/chat.slice'
import { dispatchMasterArtifactReport } from './engine-event-artifact-handlers.helper'
import {
  handleDelegationFinished,
  handleDelegationStarted,
  handleDelegationSynthesisDelta,
} from './engine-event-delegation-handlers.helper'
import {
  asNumber,
  asString,
  asStringOrNull,
  asStringOrUndefined,
  composeTurnEntityId,
  extractIds,
} from './engine-event-primitives.helper'
import {
  handleAgentToolCall,
  handleAgentToolResult,
  handleToolProgress,
} from './engine-event-tool-handlers.helper'
import {
  findEntityIdByMessageRunId,
  isActiveConversation,
  isEntityCreated,
  type WebSocketStoreApi,
} from './websocket-store.helper'

export interface StreamHandlerCallbacks {
  onNewConversation?: (conversationId: string, messageId: string) => void
  onStreamError?: (error: string, messageId: string) => void
  onConversationComplete?: (conversationId: string) => void
}

let streamCallbacks: StreamHandlerCallbacks = {}

export const setStreamHandlerCallbacks = (callbacks: StreamHandlerCallbacks): void => {
  streamCallbacks = callbacks
}

const handleRunStarted = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const ids = extractIds(event)
  if (!ids) return
  const { conversationId, messageId } = ids

  const isNewConversation = !isActiveConversation(store, conversationId)

  store.dispatch(setActiveConversation({ id: conversationId }))
  store.dispatch(markConversationAsLocallyCreated({ conversationId }))
  store.dispatch(movePendingToConversation({ targetConversationId: conversationId }))
  store.dispatch(confirmAllPendingInConversation({ conversationId }))
  store.dispatch(setOrchestrationActive({ conversationId }))

  const tagList: Parameters<typeof agentsApiSlice.util.invalidateTags>[0] = [
    { type: 'Agencies', id: 'RECENTLY_USED' },
  ]
  if (isNewConversation) {
    tagList.push('Conversations')
  }
  store.dispatch(agentsApiSlice.util.invalidateTags(tagList))

  streamCallbacks.onNewConversation?.(conversationId, messageId)
}

const handleAgentTextDelta = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const ids = extractIds(event)
  if (!ids) return
  if (!isActiveConversation(store, ids.conversationId)) return

  const delta = asString(event.data.delta)
  if (!delta) return

  const threadId = asStringOrUndefined(event.agent_execution_thread_id)
  const agentSlug = asStringOrUndefined(event.agent_node_slug)
  const messageRunId = asString(event.data.message_run_id)
  const streamId = composeTurnEntityId(threadId, messageRunId, ids.messageId)

  if (!isEntityCreated(store, streamId)) {
    store.dispatch(
      createContentStreamEntity({
        conversationId: ids.conversationId,
        parentEventId: ids.messageId,
        eventId: streamId,
        agentName: agentSlug,
        threadId,
        messageRunId: messageRunId || undefined,
        turnStartedAt: event.ts,
        agentLabel: asStringOrUndefined(event.data.agent_label),
        parentThreadId: asStringOrNull(event.data.parent_thread_id),
      })
    )
  }

  store.dispatch(appendToItemStream({ itemId: streamId, content: delta }))
}

const handleAgentLlmError = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const ids = extractIds(event)
  if (!ids) return
  if (!isActiveConversation(store, ids.conversationId)) return

  const message = asString(event.data.user_friendly_message) || 'LLM error'
  store.dispatch(failItemStreaming({ itemId: ids.messageId, errorMessage: message }))
}

const resolveEntityForRunId = (store: WebSocketStoreApi, event: EngineEvent): string | null => {
  const ids = extractIds(event)
  if (!ids) return null
  if (!isActiveConversation(store, ids.conversationId)) return null
  const messageRunId = asString(event.data.message_run_id)
  return findEntityIdByMessageRunId(store, messageRunId)
}

const handleAgentFinished = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const entityId = resolveEntityForRunId(store, event)
  if (!entityId) return
  store.dispatch(finalizeEntityStreaming({ entityId }))
}

const handleRunCompleted = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const ids = extractIds(event)
  if (!ids) return
  if (!isActiveConversation(store, ids.conversationId)) return

  dispatchMasterArtifactReport(store, event, ids.conversationId, ids.messageId)

  store.dispatch(clearOrchestrationActive({ conversationId: ids.conversationId }))
  store.dispatch(completeAllConversationEntityStreaming({ conversationId: ids.conversationId }))
  streamCallbacks.onConversationComplete?.(ids.conversationId)
}

const handleRunFailed = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const ids = extractIds(event)
  if (!ids) return
  if (!isActiveConversation(store, ids.conversationId)) return

  const errorMessage =
    asString(event.data.user_friendly_message) ||
    asString(event.data.details) ||
    asString(event.data.reason) ||
    'Run failed'

  store.dispatch(clearOrchestrationActive({ conversationId: ids.conversationId }))
  store.dispatch(failItemStreaming({ itemId: ids.messageId, errorMessage }))
  streamCallbacks.onStreamError?.(errorMessage, ids.messageId)
}

const handleRunStopped = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const ids = extractIds(event)
  if (!ids) return
  if (!isActiveConversation(store, ids.conversationId)) return

  store.dispatch(clearConversationStopping({ conversationId: ids.conversationId }))
  store.dispatch(clearOrchestrationActive({ conversationId: ids.conversationId }))
  store.dispatch(completeAllConversationEntityStreaming({ conversationId: ids.conversationId }))
  store.dispatch(clearLocallyCreatedFlag({ conversationId: ids.conversationId }))
  streamCallbacks.onConversationComplete?.(ids.conversationId)
}

const handleBalanceUpdated = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const balanceMicros = asNumber(event.data.new_balance_micros, 0)
  store.dispatch(updateTenantBalance(String(balanceMicros)))

  if (microsToDollars(balanceMicros) > 0) return

  const conversationId = asString(event.data.conversation_id)
  if (!conversationId) return
  if (!isActiveConversation(store, conversationId)) return

  store.dispatch(clearConversationStopping({ conversationId }))
  store.dispatch(clearOrchestrationActive({ conversationId }))
  store.dispatch(completeAllConversationEntityStreaming({ conversationId }))
}

const handleToolReportHtml = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const ids = extractIds(event)
  if (!ids) return
  if (!isActiveConversation(store, ids.conversationId)) return

  const html = asString(event.data.html)
  const producer =
    asString(event.data.tool_slug) || asStringOrUndefined(event.agent_node_slug) || ''

  store.dispatch(
    addAgentReportEntity({
      conversationId: ids.conversationId,
      parentEventId: ids.messageId,
      agentId: producer,
      agentName: producer,
      reportContent: html,
      eventId: event.event_id,
      reportVersion: 1,
      reportId: asString(event.data.tool_call_id) || event.event_id,
      turnStartedAt: event.ts,
    })
  )
}

const handleRunResumed = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const ids = extractIds(event)
  if (!ids) return
  if (!isActiveConversation(store, ids.conversationId)) return

  store.dispatch(setOrchestrationActive({ conversationId: ids.conversationId }))
}

type EngineEventHandler = (store: WebSocketStoreApi, event: EngineEvent) => void

const handlers: Record<string, EngineEventHandler> = {
  'run.started': handleRunStarted,
  'run.completed': handleRunCompleted,
  'run.failed': handleRunFailed,
  'run.cancelled': handleRunStopped,
  'run.paused': handleRunStopped,
  'run.resumed': handleRunResumed,
  'agent.text_delta': handleAgentTextDelta,
  'agent.tool_call': handleAgentToolCall,
  'agent.tool_result': handleAgentToolResult,
  'agent.finished': handleAgentFinished,
  'agent.llm_error': handleAgentLlmError,
  'delegation.started': handleDelegationStarted,
  'delegation.synthesis_delta': handleDelegationSynthesisDelta,
  'delegation.synthesis_finished': handleDelegationFinished,
  'tool.progress': handleToolProgress,
  'tool.report_html': handleToolReportHtml,
  'balance.updated': handleBalanceUpdated,
}

export const isEngineEvent = (data: unknown): data is EngineEvent =>
  typeof data === 'object' && data !== null && 'kind' in data && 'run_id' in data

export const handleEngineEvent = (store: WebSocketStoreApi, event: EngineEvent): boolean => {
  const handler = handlers[event.kind]
  if (!handler) {
    logger.warn('Unhandled engine event kind', {
      kind: event.kind,
      runId: event.run_id,
    })
    return false
  }
  handler(store, event)
  return true
}
